/**
 * Achievers of the Month — horizontal card strip on dark mesh (reference-inspired).
 */
import { useEffect, useMemo, useState } from 'react';
import { Award, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import api from '../../utils/api';
import { uploadHref } from '../../utils/uploadHref';

const ROLE_ORDER = ['BDA', 'BDM', 'BDE', 'Manager', 'Other'];

const ROLE_DESIGNATION = {
    BDA: 'Business Development Associate',
    BDE: 'Business Development Executive',
    BDM: 'Business Development Manager',
};

function monthLabel(key) {
    if (!key) return '';
    const [y, m] = key.split('-');
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return `${months[Number(m) - 1] || ''} ${y}`;
}

function AchieverCard({ achiever }) {
    const designation = ROLE_DESIGNATION[achiever.roleType] || achiever.roleType;
    const note = (achiever.note || '').trim();

    return (
        <article className="group flex w-[280px] sm:w-[300px] shrink-0 flex-col overflow-hidden rounded-2xl bg-white shadow-[0_16px_48px_-16px_rgba(0,0,0,0.35)] ring-1 ring-white/20">
            <div className="relative aspect-[4/5] overflow-hidden bg-gradient-to-br from-brand-teal-50 to-ink-100">
                {achiever.photoUrl ? (
                    <img
                        src={uploadHref(achiever.photoUrl)}
                        alt={achiever.name}
                        className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
                        loading="lazy"
                    />
                ) : (
                    <div className="grid h-full w-full place-items-center">
                        <span className="font-heading text-6xl font-extrabold text-brand-teal/20">
                            {achiever.name?.[0]?.toUpperCase() || '?'}
                        </span>
                    </div>
                )}
                <span className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full bg-brand-gold text-brand-dark shadow-lg">
                    <Star className="h-4 w-4 fill-current" />
                </span>
            </div>
            <div className="flex flex-1 flex-col p-4">
                <h3 className="font-heading font-bold text-lg text-ink-900 leading-tight">{achiever.name}</h3>
                <p className="mt-1 text-xs font-medium text-brand-teal-700">{designation}</p>
                <p className="mt-3 text-[12px] text-ink-500 leading-relaxed line-clamp-2 flex-1">
                    {note || 'Excellence in performance this month.'}
                </p>
            </div>
        </article>
    );
}

export default function LandingAchieversSection() {
    const [items, setItems] = useState([]);
    const [monthKey, setMonthKey] = useState('');
    const [loaded, setLoaded] = useState(false);
    const [scrollRef, setScrollRef] = useState(null);

    useEffect(() => {
        api.get('/achievers')
            .then(r => {
                setItems(r.data?.items || []);
                setMonthKey(r.data?.monthKey || '');
            })
            .catch(() => {})
            .finally(() => setLoaded(true));
    }, []);

    const ordered = useMemo(() => {
        const rank = rt => {
            const i = ROLE_ORDER.indexOf(rt);
            return i === -1 ? ROLE_ORDER.length : i;
        };
        return [...items].sort((a, b) => rank(a.roleType) - rank(b.roleType));
    }, [items]);

    const scroll = dir => {
        if (!scrollRef) return;
        scrollRef.scrollBy({ left: dir * 320, behavior: 'smooth' });
    };

    if (!loaded || ordered.length === 0) return null;

    return (
        <section
            id="achievers"
            className="relative scroll-mt-24 overflow-hidden landing-mesh-achievers landing-grain"
            aria-labelledby="achievers-heading"
        >
            <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16 lg:py-20">
                <div className="flex flex-wrap items-end justify-between gap-4 mb-10" data-aos="fade-up">
                    <div>
                        <span className="lp-chip lp-chip--gold mb-4">
                            <Award className="h-3.5 w-3.5" />
                            Employee recognition
                        </span>
                        <h2 id="achievers-heading" className="font-heading font-extrabold text-2xl sm:text-3xl lg:text-4xl text-white tracking-tight">
                            Achievers of the month
                        </h2>
                        {monthKey && (
                            <p className="mt-2 text-sm text-white/55">{monthLabel(monthKey)}</p>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => scroll(-1)}
                            aria-label="Previous achiever"
                            className="grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white ring-1 ring-white/20 transition hover:bg-white/20"
                        >
                            <ChevronLeft className="h-5 w-5" />
                        </button>
                        <button
                            type="button"
                            onClick={() => scroll(1)}
                            aria-label="Next achiever"
                            className="grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white ring-1 ring-white/20 transition hover:bg-white/20"
                        >
                            <ChevronRight className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                <div
                    ref={setScrollRef}
                    className="flex gap-5 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide -mx-5 px-5 sm:-mx-8 sm:px-8 lg:-mx-12 lg:px-12"
                    data-aos="fade-up"
                    data-aos-delay="80"
                >
                    {ordered.map((a, i) => (
                        <div key={a._id || `${a.roleType}-${i}`} className="snap-start">
                            <AchieverCard achiever={a} />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
